/**
 * La aritmetica del catalogo y del stock, separada de la UI.
 *
 * POR QUE ESTE ARCHIVO EXISTE
 * ---------------------------
 * Dos cosas de este modulo deciden plata y ninguna de las dos se ve hasta que
 * ya es tarde:
 *
 *   1. EL PRECIO DE VENTA NO SE ESCRIBE. Lo calcula un trigger de la base a
 *      partir del costo, del markup, de las monedas y del redondeo comercial.
 *      Quien carga un producto no elige el precio: elige los insumos del
 *      precio. Si la pantalla no le muestra el resultado ANTES de guardar, el
 *      producto queda con un numero que nadie decidio y que recien se descubre
 *      vendiendo.
 *
 *   2. EL STOCK VALE EN DOS MONEDAS A LA VEZ. La misma mercaderia son $ 40M o
 *      son US$ 26.000, segun con que se mire. Son dos lecturas del mismo
 *      inventario, no dos plata distintas: sumarlas da un numero que no existe.
 *
 * `simularPrecio()` no reimplementa nada: llama a `calcularPrecio()` de
 * `@tora/shared`, que es la misma funcion que replica el SQL de
 * `calcular_precio` y que tiene 2.333 casos de test detras. Lo que agrega es lo
 * que la funcion pura no puede saber: que hacer cuando el formulario todavia
 * esta a medio llenar, y de cuanto se aparta un precio manual del que
 * corresponderia.
 *
 * Nada de aca toca React ni el DOM.
 */
import {
  aplicarRedondeo,
  calcularPrecio,
  convertir,
  despejarPrecio,
  formatear,
  formatearNumero,
  redondear,
  type Moneda,
} from "@tora/shared";

import {
  armarModeloElegido,
  equipoDesdeProducto,
  esEquipo,
  nombreDeEquipo,
} from "@/components/iphones/equipo";
import {
  aNumero,
  type CambiosProducto,
  type EstadoStock,
  type FilaReposicion,
  type FiltrosProductos,
  type MarkupTipo,
  type MovimientoStock,
  type NuevoProducto,
  type Numerico,
  type Producto,
  type Redondeo,
  type StockPorFamilia,
} from "@/lib/tipos";

import { seccionDeFamilia, type Seccion } from "./secciones";

// ─────────────────────────────────────────────────────────────────────────────
// El formulario de producto
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El estado del formulario de alta/edicion.
 *
 * Difiere del `Producto` del backend en tres cosas, y las tres son a proposito:
 *
 *  - Los importes son `number | null`. `null` es "todavia no se cargo", que no
 *    es lo mismo que cero. Un costo cero es un producto regalado; un costo sin
 *    cargar es un formulario a medias. El `<InputImporte />` ya trabaja asi.
 *  - Los ids de rubro y proveedor son string, porque salen de un `<Select>`.
 *  - `usaPrecioManual` es un booleano aparte de `precioManual`. Destildar la
 *    casilla tiene que poder devolver el producto al precio calculado sin que
 *    haya que borrar el numero a mano.
 *
 * `stock_actual` NO esta. No es un olvido: lo mantienen los movimientos, el
 * backend rechaza el PATCH que lo traiga, y ponerlo en un formulario seria
 * ofrecer una edicion que no existe.
 */
export interface FormularioProducto {
  /**
   * El nombre GUARDADO. En un equipo no se escribe: lo arma `nombreDeEquipo`
   * con el modelo y la memoria de mas abajo, y el campo no aparece en el
   * formulario. En todo lo demas —un cable, un perfume— sigue siendo lo que
   * alguien escribe, porque no hay nada de donde derivarlo.
   */
  nombre: string;
  /**
   * Esta ficha es la de un telefono.
   *
   * Decide que se pregunta: un equipo se describe (modelo, memoria, color) y un
   * accesorio se nombra. Se contesta al abrir, mirando el producto guardado
   * (`esEquipo`), y no se puede cambiar desde el formulario: convertir un cable
   * en un iPhone no es una edicion, es un producto nuevo.
   */
  esEquipo: boolean;
  /** Para el "(USADO)" del nombre. Lo decide la ficha, no el formulario. */
  esUsado: boolean;
  /** "17 PRO MAX". Solo se usa si `esEquipo`. */
  modelo: string;
  memoriaGb: number | null;
  /** El color de Apple, en mayusculas: "BLACK TITANIUM". */
  color: string;
  codigoInterno: string;
  codigoExterno: string;
  descripcion: string;
  /** "" = sin rubro. */
  rubroId: string;
  /** Texto libre. "" = sin marca, que es un estado legitimo: no todo tiene. */
  marca: string;
  /** "" = sin proveedor. */
  proveedorId: string;
  costo: number | null;
  costoMoneda: Moneda;
  markupTipo: MarkupTipo;
  markupValor: number | null;
  /** Solo se usa cuando `markupTipo` es FIJO. */
  markupMoneda: Moneda;
  precioMoneda: Moneda;
  redondeo: Redondeo;
  usaPrecioManual: boolean;
  precioManual: number | null;
  stockMinimo: number | null;
  stockIdeal: number | null;
  publicado: boolean;
  aPedido: boolean;
  activo: boolean;
}

/**
 * El producto nuevo arranca en pesos con 30% de markup y redondeo al mil.
 *
 * Ninguno de esos valores es neutro y por eso estan todos a la vista en el
 * formulario: un default escondido es la forma mas comun de que un producto
 * quede cotizado en una moneda que nadie eligio.
 */
export const FORMULARIO_VACIO: FormularioProducto = {
  nombre: "",
  esEquipo: false,
  esUsado: false,
  modelo: "",
  memoriaGb: null,
  color: "",
  codigoInterno: "",
  codigoExterno: "",
  descripcion: "",
  rubroId: "",
  marca: "",
  proveedorId: "",
  costo: null,
  costoMoneda: "USD",
  markupTipo: "PORCENTAJE",
  markupValor: 30,
  markupMoneda: "USD",
  precioMoneda: "ARS",
  redondeo: "MIL",
  usaPrecioManual: false,
  precioManual: null,
  stockMinimo: null,
  stockIdeal: null,
  publicado: false,
  aPedido: false,
  activo: true,
};

/** Pasa un producto del backend al estado del formulario. */
export function desdeProducto(p: Producto): FormularioProducto {
  // El modelo no tiene columna: vive adentro del nombre armado, y de ahi se lee
  // de vuelta para dejar los chips donde corresponde.
  const equipo = equipoDesdeProducto(p);
  return {
    nombre: p.nombre,
    esEquipo: esEquipo(p),
    esUsado: p.es_usado,
    modelo: armarModeloElegido(equipo),
    memoriaGb: equipo.memoriaGb,
    color: p.color ?? "",
    codigoInterno: p.codigo_interno,
    codigoExterno: p.codigo_externo ?? "",
    descripcion: p.descripcion ?? "",
    rubroId: p.rubro_id === null ? "" : String(p.rubro_id),
    marca: p.marca ?? "",
    proveedorId: p.proveedor_id === null ? "" : String(p.proveedor_id),
    costo: aNumero(p.costo),
    costoMoneda: p.costo_moneda,
    markupTipo: p.markup_tipo,
    markupValor: aNumero(p.markup_valor),
    // Si el markup es por porcentaje la moneda viene null. Se precarga con la
    // del costo para que, si alguien pasa a FIJO, el default sea el razonable.
    markupMoneda: p.markup_moneda ?? p.costo_moneda,
    precioMoneda: p.precio_moneda,
    redondeo: p.redondeo,
    usaPrecioManual: p.precio_manual !== null,
    precioManual: p.precio_manual === null ? null : aNumero(p.precio_manual),
    stockMinimo: aNumero(p.stock_minimo),
    stockIdeal: aNumero(p.stock_ideal),
    publicado: p.publicado,
    aPedido: p.a_pedido,
    activo: p.activo,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// El simulador de precio
// ─────────────────────────────────────────────────────────────────────────────

export interface Simulacion {
  /** Si con lo cargado hasta ahora ya se puede calcular un precio. */
  calculable: boolean;
  /** Que falta para poder calcular. `null` cuando `calculable`. */
  motivo: string | null;
  /** El costo llevado a la moneda de venta. */
  costoBase: number;
  /** Lo que suma el markup, en la moneda de venta. */
  markupImporte: number;
  /** Precio antes del redondeo comercial. */
  precioBruto: number;
  /** El precio que sale del motor. Es la referencia, aunque haya manual. */
  precioCalculado: number;
  /** Lo que agrega el redondeo comercial sobre el bruto. Siempre >= 0. */
  ajusteRedondeo: number;
  /** El precio que va a quedar guardado: el manual si lo hay, si no el calculado. */
  precioFinal: number;
  /** `true` si un precio manual piso el calculo. */
  esManual: boolean;
  /** Manual menos calculado. Negativo = se esta vendiendo mas barato. */
  desvio: number;
  /** El desvio en porcentaje del calculado. 0 si el calculado es 0. */
  desvioPct: number;
  /**
   * `true` si mover el dolar mueve este precio: o el costo esta en otra moneda
   * que la venta, o el markup fijo lo esta. Es exactamente la condicion del
   * `where` de `recalcular_precios()`.
   */
  dependeDelDolar: boolean;
  /** Ganancia sobre el costo, en la moneda de venta. */
  ganancia: number;
  /** Margen sobre el precio de venta, en porcentaje. */
  margenPct: number;
}

const SIMULACION_VACIA: Omit<Simulacion, "calculable" | "motivo" | "dependeDelDolar"> = {
  costoBase: 0,
  markupImporte: 0,
  precioBruto: 0,
  precioCalculado: 0,
  ajusteRedondeo: 0,
  precioFinal: 0,
  esManual: false,
  desvio: 0,
  desvioPct: 0,
  ganancia: 0,
  margenPct: 0,
};

/**
 * El precio que va a quedar, calculado con lo que hay cargado en el formulario.
 *
 * Se llama en cada tecla, mientras se escribe el costo y el markup, y el
 * resultado se muestra al lado. Esa es toda la razon de que exista esta funcion
 * separada de `calcularPrecio()`: la de `@tora/shared` necesita numeros
 * completos y tira una excepcion si un markup FIJO viene sin moneda. Un
 * formulario a medio llenar no puede romper la pantalla; tiene que decir que
 * falta.
 *
 * El precio manual NO se le pasa a `calcularPrecio()`: se calcula igual el
 * precio que corresponderia y despues se lo pisa aca. Asi la referencia
 * ("deberia costar X") queda disponible para mostrarla al lado, que es lo unico
 * que hace visible de cuanto se esta apartando.
 *
 * LA UNICA GRIETA CONOCIDA ENTRE ESTE NUMERO Y EL DE LA BASE
 * ----------------------------------------------------------
 * El trigger usa `cotizacion_vigente(coalesce(costo_actualizado_at, now()))` y
 * aca llega la `cotizacion_vigente()` de hoy que trae `/config`. Son el mismo
 * numero salvo en un caso: producto cuyo costo entro con un ingreso de stock
 * fechado hacia atras, con la cotizacion en modo AUTO y con el dolar de ese dia
 * distinto al de hoy. Ahi la vista previa muestra el precio con el dolar de hoy
 * y la base lo escribe con el de esa fecha. Es una diferencia de horas de
 * cotizacion sobre un producto que ya estaba mal fechado, no un error de
 * calculo: el motor es el mismo.
 */
export function simularPrecio(form: FormularioProducto, cotizacion: number): Simulacion {
  const dependeDelDolar =
    form.costoMoneda !== form.precioMoneda ||
    (form.markupTipo === "FIJO" && form.markupMoneda !== form.precioMoneda);

  const base = { ...SIMULACION_VACIA, dependeDelDolar };

  if (form.costo === null) {
    return { ...base, calculable: false, motivo: "Cargá el costo para ver el precio de venta." };
  }
  if (form.costo < 0) {
    return { ...base, calculable: false, motivo: "El costo no puede ser negativo." };
  }
  if (form.markupValor === null) {
    return {
      ...base,
      calculable: false,
      motivo:
        form.markupTipo === "PORCENTAJE"
          ? "Cargá el porcentaje de markup."
          : "Cargá cuánto le sumás de markup.",
    };
  }
  // La base tambien lo rechaza (`markup_fijo_requiere_moneda`) y el backend
  // devuelve 400. Un importe sin moneda no se puede sumar a un costo que esta
  // en otra.
  if (form.markupTipo === "FIJO" && !form.markupMoneda) {
    return { ...base, calculable: false, motivo: "Un markup fijo necesita su moneda." };
  }
  if (dependeDelDolar && cotizacion <= 0) {
    return {
      ...base,
      calculable: false,
      motivo:
        "Sin cotización cargada no se puede pasar de una moneda a la otra. Revisá Configuración.",
    };
  }

  const calculo = calcularPrecio({
    costo: form.costo,
    costoMoneda: form.costoMoneda,
    markupTipo: form.markupTipo,
    markupValor: form.markupValor,
    markupMoneda: form.markupMoneda,
    precioMoneda: form.precioMoneda,
    redondeo: form.redondeo,
    // A proposito no va: se quiere el precio que CORRESPONDE, para poder
    // compararlo con el manual.
    precioManual: null,
    cotizacion,
  });

  const esManual = form.usaPrecioManual && form.precioManual !== null;
  const precioFinal = esManual ? redondear(form.precioManual as number) : calculo.precioVenta;
  const desvio = redondear(precioFinal - calculo.precioVenta);
  const desvioPct = calculo.precioVenta > 0 ? redondear((desvio / calculo.precioVenta) * 100) : 0;
  const ganancia = redondear(precioFinal - calculo.costoBase);
  const margenPct = precioFinal > 0 ? redondear((ganancia / precioFinal) * 100) : 0;

  return {
    calculable: true,
    motivo: null,
    costoBase: calculo.costoBase,
    markupImporte: calculo.markupImporte,
    precioBruto: calculo.precioBruto,
    precioCalculado: calculo.precioVenta,
    ajusteRedondeo: redondear(calculo.precioVenta - calculo.precioBruto),
    precioFinal,
    esManual,
    desvio,
    desvioPct,
    dependeDelDolar,
    ganancia,
    margenPct,
  };
}

/**
 * Que cambia en el formulario cuando alguien ESCRIBE el precio de venta.
 *
 * Escribir el precio no es lo mismo que fijarlo. El caso comun es el del que
 * sabe a cuanto vende y no cuanto gana: ahi el numero escrito se usa para
 * DESPEJAR el markup que lo produce, y el producto sigue repriciandose cuando
 * se mueve el costo o el dolar. Fijarlo es otra decision, y tiene su casilla.
 *
 * Sin costo cargado no hay margen que despejar. En vez de inventar uno se fija
 * el precio, que es lo unico honesto que se puede hacer con ese numero.
 *
 * Devuelve el PARCHE, no el formulario entero: lo consumen los dos editores —el
 * corto y el completo— y ninguno de los dos deberia reimplementar esta regla.
 */
export function precioEscritoA(
  form: FormularioProducto,
  valor: number | null,
  cotizacion: number,
): Partial<FormularioProducto> {
  if (form.usaPrecioManual) return { precioManual: valor };
  if (valor === null) return {};

  // Con el margen solo, de dos decimales, la vuelta no cerraba: escribir
  // US$ 350 sobre un costo de US$ 245 guardaba 42,86 % y el precio salia
  // US$ 350,01. `despejarPrecio` trae tambien el redondeo que lo reproduce.
  const despeje = despejarPrecio({
    costo: form.costo ?? 0,
    costoMoneda: form.costoMoneda,
    precioDeseado: valor,
    precioMoneda: form.precioMoneda,
    markupTipo: form.markupTipo,
    markupMoneda: form.markupMoneda,
    redondeo: form.redondeo,
    cotizacion,
  });

  if (despeje === null) return { usaPrecioManual: true, precioManual: valor };
  return despeje.redondeo === form.redondeo
    ? { markupValor: despeje.markupValor }
    : { markupValor: despeje.markupValor, redondeo: despeje.redondeo };
}

/**
 * Que cambia al tildar o destildar «Fijar este precio».
 *
 * Destildar solo apagaba la casilla, y el precio pasaba a salir del margen que
 * hubiera guardado: en lo cargado por el alta guiada ese margen era cero, y el
 * precio caia al costo. Destildar es «que siga al costo desde ESTE precio»:
 * se despeja el margen que lo da, como si se lo acabara de escribir. Sin costo
 * no hay margen que despejar y la casilla se apaga sola, como antes.
 */
export function fijarElPrecio(
  form: FormularioProducto,
  fijar: boolean,
  cotizacion: number,
): Partial<FormularioProducto> {
  if (fijar || form.precioManual === null) return { usaPrecioManual: fijar };
  const suelto = { ...form, usaPrecioManual: false };
  const parche = precioEscritoA(suelto, form.precioManual, cotizacion);
  if (parche.usaPrecioManual) return { usaPrecioManual: false };
  return { ...parche, usaPrecioManual: false };
}

/**
 * Lo que muestra el campo del precio: el fijado si lo hay, y si no el que sale
 * del calculo. Asi escribir arriba y escribir abajo hablan del mismo numero.
 */
export function precioEnElCampo(form: FormularioProducto, simulacion: Simulacion): number | null {
  if (form.usaPrecioManual) return form.precioManual;
  return simulacion.calculable ? simulacion.precioCalculado : null;
}

/**
 * Lo que dice el campo del precio escrito, que cambia por completo segun este
 * fijado o no. Es la unica forma de que se entienda que son dos cosas
 * distintas sin escribir un parrafo arriba.
 */
export function ayudaDelPrecioEscrito(form: FormularioProducto, simulacion: Simulacion): string {
  if (form.usaPrecioManual) {
    return simulacion.calculable
      ? `Fijo. Por el costo y el markup debería costar ${formatear(simulacion.precioCalculado, form.precioMoneda)}.`
      : "Fijo: no se mueve aunque cambien el costo o el dólar.";
  }
  if (!(form.costo !== null && form.costo > 0)) {
    return "Sin costo cargado, escribir el precio lo deja fijo: no hay margen que despejar.";
  }
  return "Se guarda el margen que da ese precio, no el número. Si sube el costo o el dólar, el precio acompaña.";
}

/**
 * Como queda un precio con cada redondeo, para mostrarlos todos juntos.
 *
 * Verlos al lado es lo que hace evidente que el redondeo comercial va
 * SIEMPRE hacia arriba: ninguna de las opciones baja el precio.
 */
export function opcionesDeRedondeo(
  precioBruto: number,
): ReadonlyArray<{ modo: Redondeo; label: string; precio: number; suma: number }> {
  return REDONDEOS_UI.map((r) => {
    const precio = aplicarRedondeo(precioBruto, r.modo);
    return { modo: r.modo, label: r.label, precio, suma: redondear(precio - precioBruto) };
  });
}

export const REDONDEOS_UI: ReadonlyArray<{ modo: Redondeo; label: string; ayuda: string }> = [
  {
    modo: "NINGUNO",
    label: "Sin redondear",
    ayuda: "El precio queda con centavos, tal como sale.",
  },
  {
    modo: "UNIDAD",
    label: "Al entero",
    ayuda:
      "Sube al próximo entero: 350,01 pasa a 351. Es el que pone el sistema al escribir el precio.",
  },
  { modo: "CIEN", label: "Al cien", ayuda: "Sube al próximo múltiplo de 100. 1.234 → 1.300." },
  {
    modo: "QUINIENTOS",
    label: "Al quinientos",
    ayuda: "Sube al próximo múltiplo de 500. 1.234 → 1.500.",
  },
  { modo: "MIL", label: "Al mil", ayuda: "Sube al próximo múltiplo de 1.000. 1.234 → 2.000." },
  {
    modo: "TERMINACION_990",
    label: "Terminado en 990",
    ayuda: "Sube al próximo x.990. 1.234 → 1.990. Solo tiene sentido en pesos.",
  },
];

export const MARKUPS_UI: ReadonlyArray<{ tipo: MarkupTipo; label: string; ayuda: string }> = [
  {
    tipo: "PORCENTAJE",
    label: "Porcentaje",
    ayuda: "Un % sobre el costo. Si sube el costo, sube el precio en la misma proporción.",
  },
  {
    tipo: "FIJO",
    label: "Monto fijo",
    ayuda: "Un importe fijo sobre el costo. Necesita su propia moneda para poder sumarse.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Validacion y armado del payload
// ─────────────────────────────────────────────────────────────────────────────

/** Lo que impide guardar. Vacio = se puede mandar. */
export function validarProducto(form: FormularioProducto, stockActual = 0): string[] {
  const errores: string[] = [];

  /*
   * UN SELLADO CON UNIDADES EN EL LOCAL TIENE COLOR.
   *
   * El editor deja poner «Todos los del modelo», que es lo correcto para la
   * fila de lista de precios —dice cuanto sale el 17 Pro y en que colores
   * viene, y queda en cero— y es lo que estaba mal para una ficha con equipos
   * adentro: saca el dato de la unica pantalla donde estaba y deja la fila
   * marcada como incompleta sin decir por que.
   *
   * Es la misma regla que el alta aplica desde que la distincion existe
   * (`validarUnidades`). Al editor nunca habia llegado, y era la ultima puerta
   * por la que una ficha podia quedarse con unidades y sin color.
   *
   * Un usado no entra: su color es obligatorio por otro lado, y la ficha de un
   * usado siempre es una unidad.
   */
  if (form.esEquipo && !form.esUsado && stockActual > 0 && !form.color.trim()) {
    errores.push(
      "¿De qué color son? Hay unidades en el local: «Todos los del modelo» es para la fila de lista de precios, que queda en cero.",
    );
  }

  /*
   * UN EQUIPO NO PIDE NOMBRE: PIDE MODELO Y MEMORIA.
   *
   * El nombre sale de esas dos respuestas, asi que exigirlo seria exigir algo
   * que el formulario no pregunta. Y al reves: un cable no tiene modelo ni
   * memoria, y lo unico que lo nombra es lo que alguien escribe.
   */
  if (form.esEquipo) {
    if (!form.modelo.trim()) errores.push("¿Qué modelo es?");
    if (form.memoriaGb === null) errores.push("Elegí la memoria.");
  } else if (!form.nombre.trim()) {
    errores.push("El producto necesita un nombre.");
  }
  if (form.costo !== null && form.costo < 0) errores.push("El costo no puede ser negativo.");
  if (form.markupValor === null) errores.push("Falta el markup.");
  if (form.usaPrecioManual && form.precioManual === null) {
    errores.push("Marcaste precio manual: escribí el precio o desmarcá la opción.");
  }
  if (form.usaPrecioManual && form.precioManual !== null && form.precioManual < 0) {
    errores.push("El precio manual no puede ser negativo.");
  }
  if (form.stockMinimo !== null && form.stockMinimo < 0) {
    errores.push("El stock mínimo no puede ser negativo.");
  }
  if (form.stockIdeal !== null && form.stockIdeal < 0) {
    errores.push("El stock ideal no puede ser negativo.");
  }

  return errores;
}

/**
 * Lo que no impide guardar pero conviene mirar antes de confirmar.
 *
 * Van separadas de los errores porque las tres son configuraciones legitimas en
 * algun caso: hay productos que se venden al costo, y hay productos que se
 * cargan sin stock ideal porque se traen a pedido.
 */
export function advertenciasProducto(form: FormularioProducto): string[] {
  const avisos: string[] = [];

  if (form.stockIdeal !== null && form.stockMinimo !== null && form.stockIdeal < form.stockMinimo) {
    avisos.push(
      "El stock ideal es menor que el mínimo: la reposición nunca va a sugerir comprar este producto.",
    );
  }
  if (form.markupValor !== null && form.markupValor <= 0 && !form.usaPrecioManual) {
    avisos.push("Con markup cero el producto se vende al costo.");
  }
  if (form.redondeo === "TERMINACION_990" && form.precioMoneda === "USD") {
    avisos.push("Terminación 990 en dólares no es una terminación comercial: revisá el redondeo.");
  }
  if (form.publicado && !form.activo) {
    avisos.push("El producto está publicado pero dado de baja: no va a aparecer en el catálogo.");
  }

  return avisos;
}

function textoONull(v: string): string | null {
  const t = v.trim();
  return t === "" ? null : t;
}

/**
 * El nombre con el que el producto queda guardado.
 *
 * En un equipo lo arma la ficha —el mismo armador que usan el alta guiada y el
 * asistente de usados—, y no hay campo donde escribirlo. En el resto del
 * catalogo es lo que se escribio.
 */
export function nombreGuardado(form: FormularioProducto): string {
  if (!form.esEquipo) return form.nombre.trim();
  return nombreDeEquipo({
    modelo: form.modelo,
    memoriaGb: form.memoriaGb,
    esUsado: form.esUsado,
  });
}

/**
 * Las columnas de la ficha. Solo viajan si el producto es un equipo: mandar
 * `color: null` en un perfume seria borrar un dato que este formulario no
 * pregunta.
 *
 * Un color vacio SI viaja como null: destildar el color de un sellado es
 * decir que la fila volvio a ser la lista de precios del modelo, y eso hay que
 * poder guardarlo.
 */
function fichaDelEquipo(form: FormularioProducto): {
  color?: string | null;
  memoria_gb?: number | null;
} {
  if (!form.esEquipo) return {};
  return { color: textoONull(form.color), memoria_gb: form.memoriaGb };
}

/**
 * El payload del alta.
 *
 * DOS COSAS QUE VAN SIEMPRE, AUNQUE PAREZCAN REDUNDANTES
 * ------------------------------------------------------
 *  - `precio_moneda`. La tabla tiene default 'ARS'. Ese default silencioso deja
 *    un iPhone cotizado en pesos que nunca se repricia cuando se mueve el
 *    dolar, y no se nota hasta que el precio quedo meses atrasado. El backend
 *    la exige explicitamente en el alta y aca va siempre.
 *  - `costo_moneda` junto al costo. Cargar 1.200 pensando en dolares sobre un
 *    producto cuya moneda de costo era ARS deja el precio mil veces por debajo.
 *
 * `precio_venta` NO va: lo calcula el trigger. Mandarlo seria pisar el motor con
 * un numero que envejece.
 */
export function aNuevoProducto(form: FormularioProducto): NuevoProducto {
  const datos: NuevoProducto = {
    nombre: nombreGuardado(form),
    ...fichaDelEquipo(form),
    precio_moneda: form.precioMoneda,
    costo: form.costo ?? 0,
    costo_moneda: form.costoMoneda,
    markup_tipo: form.markupTipo,
    markup_valor: form.markupValor ?? 0,
    // Un markup por porcentaje no tiene moneda: es una proporcion del costo.
    markup_moneda: form.markupTipo === "FIJO" ? form.markupMoneda : null,
    redondeo: form.redondeo,
    precio_manual: form.usaPrecioManual ? form.precioManual : null,
    rubro_id: form.rubroId === "" ? null : Number(form.rubroId),
    marca: textoONull(form.marca),
    proveedor_id: form.proveedorId === "" ? null : Number(form.proveedorId),
    codigo_externo: textoONull(form.codigoExterno),
    descripcion: textoONull(form.descripcion),
    stock_minimo: form.stockMinimo ?? 0,
    stock_ideal: form.stockIdeal ?? 0,
    publicado: form.publicado,
    a_pedido: form.aPedido,
    activo: form.activo,
  };

  // Si no se escribe, lo genera el trigger `set_codigo_producto` (P000041...).
  const codigo = form.codigoInterno.trim();
  if (codigo) datos.codigo_interno = codigo;

  return datos;
}

/**
 * El PATCH de edicion: solo lo que cambio.
 *
 * Con dos acoplamientos que el backend exige y que un diff campo por campo
 * romperia:
 *
 *  - Si cambia el costo, va tambien `costo_moneda`. Cambiar el numero sin
 *    confirmar la unidad es exactamente el movimiento que rompe, y el backend
 *    lo rechaza (`exigeMonedaDelCosto`).
 *  - Si se toca cualquier parte del markup, van las tres: tipo, valor y moneda.
 *    Un FIJO sin moneda lo rechaza el backend y ademas la base.
 *
 * `stock_actual` no aparece por ningun lado: el PATCH que lo traiga se rechaza
 * con un 400 que apunta a /inventario, y con razon.
 */
export function aCambiosProducto(form: FormularioProducto, original: Producto): CambiosProducto {
  const cambios: CambiosProducto = {};

  const nombre = nombreGuardado(form);
  if (nombre !== original.nombre) cambios.nombre = nombre;

  if (form.esEquipo) {
    const color = textoONull(form.color);
    if (color !== (original.color ?? null)) cambios.color = color;
    if (form.memoriaGb !== (original.memoria_gb ?? null)) cambios.memoria_gb = form.memoriaGb;
  }

  const codigoExterno = textoONull(form.codigoExterno);
  if (codigoExterno !== original.codigo_externo) cambios.codigo_externo = codigoExterno;

  const descripcion = textoONull(form.descripcion);
  if (descripcion !== original.descripcion) cambios.descripcion = descripcion;

  const rubroId = form.rubroId === "" ? null : Number(form.rubroId);
  if (rubroId !== original.rubro_id) cambios.rubro_id = rubroId;

  const marca = textoONull(form.marca);
  if (marca !== original.marca) cambios.marca = marca;

  const proveedorId = form.proveedorId === "" ? null : Number(form.proveedorId);
  if (proveedorId !== original.proveedor_id) cambios.proveedor_id = proveedorId;

  const costo = form.costo ?? 0;
  if (costo !== aNumero(original.costo) || form.costoMoneda !== original.costo_moneda) {
    cambios.costo = costo;
    cambios.costo_moneda = form.costoMoneda;
  }

  const markupValor = form.markupValor ?? 0;
  const markupMoneda = form.markupTipo === "FIJO" ? form.markupMoneda : null;
  if (
    form.markupTipo !== original.markup_tipo ||
    markupValor !== aNumero(original.markup_valor) ||
    markupMoneda !== original.markup_moneda
  ) {
    cambios.markup_tipo = form.markupTipo;
    cambios.markup_valor = markupValor;
    cambios.markup_moneda = markupMoneda;
  }

  if (form.precioMoneda !== original.precio_moneda) cambios.precio_moneda = form.precioMoneda;
  if (form.redondeo !== original.redondeo) cambios.redondeo = form.redondeo;

  const precioManual = form.usaPrecioManual ? form.precioManual : null;
  const manualOriginal = original.precio_manual === null ? null : aNumero(original.precio_manual);
  if (precioManual !== manualOriginal) cambios.precio_manual = precioManual;

  const stockMinimo = form.stockMinimo ?? 0;
  if (stockMinimo !== aNumero(original.stock_minimo)) cambios.stock_minimo = stockMinimo;

  const stockIdeal = form.stockIdeal ?? 0;
  if (stockIdeal !== aNumero(original.stock_ideal)) cambios.stock_ideal = stockIdeal;

  if (form.publicado !== original.publicado) cambios.publicado = form.publicado;
  if (form.aPedido !== original.a_pedido) cambios.a_pedido = form.aPedido;
  if (form.activo !== original.activo) cambios.activo = form.activo;

  return cambios;
}

// ─────────────────────────────────────────────────────────────────────────────
// Filtros de la lista de productos
// ─────────────────────────────────────────────────────────────────────────────

export interface FiltrosUi {
  texto: string;
  rubroId: string;
  /** El nombre de la marca, tal cual esta escrito. "" = sin filtrar. */
  marca: string;
  proveedorId: string;
  /** "si" = activos, "no" = dados de baja. El backend no ofrece "todos". */
  activo: "si" | "no";
  /** "" = sin filtrar. */
  publicado: "" | "si" | "no";
}

export const FILTROS_INICIALES: FiltrosUi = {
  texto: "",
  rubroId: "",
  marca: "",
  proveedorId: "",
  activo: "si",
  publicado: "",
};

/**
 * Los filtros que resuelve el servidor: SOLO los que cambian que productos
 * existen para esta pantalla.
 *
 * El texto NO viaja: el `q` del backend busca solo por nombre (para buscar por
 * codigo esta `/precios`, que ademas contesta si hay stock). Aca hace falta
 * buscar tambien por codigo interno y por codigo de proveedor, asi que el texto
 * se filtra sobre lo que ya se trajo. Con un catalogo de este tamanio eso es
 * instantaneo y ademas no pide una consulta por tecla.
 *
 * LA FAMILIA Y EL PROVEEDOR TAMPOCO VIAJAN, por el mismo motivo y por dos mas:
 *
 *   · Los numeros de las solapas se cuentan sobre lo que trajo el servidor. Si
 *     el servidor ya venia filtrado por familia, elegir «Mac» dejaba a todas las
 *     otras solapas diciendo 0. Un contador que cambia segun un filtro que no se
 *     ve desde esa solapa es un contador que miente.
 *   · Las OPCIONES de esos dos filtros salen de los productos a la vista. Para
 *     poder ofrecer «los proveedores que tienen algo en esta seccion» hace falta
 *     el conjunto sin filtrar por proveedor, que es justo lo que el servidor no
 *     puede devolver si ya lo filtro.
 */
export function filtrosParaBackend(f: FiltrosUi): FiltrosProductos {
  return {
    activo: f.activo === "si",
    ...(f.publicado ? { publicado: f.publicado === "si" } : {}),
  };
}

function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Busca por nombre, marca, los dos codigos, el color y el IMEI. Sin acentos y
 * sin mayusculas. La marca entra porque desde 0044 es un campo y ya no esta
 * siempre en el nombre: un cargador Samsung puede llamarse «20W USB-C».
 *
 * EL COLOR Y EL IMEI ENTRAN PORQUE SALIERON DEL NOMBRE
 * ----------------------------------------------------
 * Una unidad usada se llamaba "IPHONE 15 PRO 128GB BLACK (USADO 88%)" y
 * buscar "black" la encontraba de casualidad: el color estaba adentro del
 * nombre. Ahora el nombre es "IPHONE 15 PRO 128GB (USADO)" y el color es un
 * campo, asi que si el buscador no lo mira, dejo de poder buscarse — y el
 * IMEI, que nunca estuvo en el nombre, tampoco se encontraba nunca.
 *
 * Es el mismo motivo por el que el texto se filtra aca y no en el backend: el
 * `q` del servidor busca solo por nombre.
 */
/**
 * Como dice el proveedor lo que la gente dice de otra forma. «IP» e «IPH» son
 * iPhone, y «PROMAX» pegado es «PRO MAX»: «cargador iphone» no encontraba
 * «CARGADOR IP 5V», que es como vienen escritos. Es la misma traduccion que
 * hace el buscador del mostrador (0126).
 */
export function sinonimosDelNombre(nombre: string): string {
  return [/\bIPH?\b/i.test(nombre) ? "iphone" : "", /PROMAX/i.test(nombre) ? "pro max" : ""].join(
    " ",
  );
}

/**
 * El color como lo dice quien busca: la ficha lo guarda como Apple («BLACK»)
 * y «iphone 15 negro» no encontraba nada. La misma tabla que el servidor
 * (`SINONIMOS_PRODUCTO` en rutas/catalogo.ts) y que el mostrador
 * (`color_en_castellano`, 0121_9d).
 */
const COLORES_EN_CASTELLANO: ReadonlyArray<[RegExp, string]> = [
  [/\b(BLACK|MIDNIGHT)\b/i, "negro negra"],
  [/\b(WHITE|STARLIGHT)\b/i, "blanco blanca"],
  [/\b(BLUE|ULTRAMARINE|INDIGO)\b/i, "azul"],
  [/\b(PURPLE|LAVENDER)\b/i, "violeta lila morado"],
  [/\b(PINK|ROSE)\b/i, "rosa rosado"],
  [/\b(GREEN|TEAL|SAGE)\b/i, "verde"],
  [/\bSILVER\b/i, "plateado plata"],
  [/\b(GRAY|GREY|GRAPHITE)\b/i, "gris"],
  [/\bRED\b/i, "rojo roja"],
  [/\bGOLD\b/i, "dorado oro"],
  [/\bYELLOW\b/i, "amarillo"],
  [/\bORANGE\b/i, "naranja"],
  [/\bTITANIUM\b/i, "titanio"],
  [/\bDESERT\b/i, "desierto"],
];

export function colorEnCastellano(color: string | null): string {
  if (!color) return "";
  return COLORES_EN_CASTELLANO.filter(([re]) => re.test(color))
    .map(([, es]) => es)
    .join(" ");
}

export function filtrarPorTexto(productos: ReadonlyArray<Producto>, texto: string): Producto[] {
  const q = normalizar(texto.trim());
  if (!q) return [...productos];
  const partes = q.split(/\s+/);

  return productos.filter((p) => {
    const heno = normalizar(
      [
        p.nombre,
        p.marca ?? "",
        p.codigo_interno,
        p.codigo_externo ?? "",
        p.color ?? "",
        colorEnCastellano(p.color),
        p.imei ?? "",
        sinonimosDelNombre(p.nombre),
      ].join(" "),
    );
    return partes.every((parte) => heno.includes(parte));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Stock: estados
// ─────────────────────────────────────────────────────────────────────────────

export const ETIQUETA_ESTADO: Record<EstadoStock, string> = {
  OK: "OK",
  BAJO: "Bajo",
  CRITICO: "Crítico",
  SIN_STOCK: "Sin stock",
  NEGATIVO: "Negativo",
};

/**
 * Que significa cada estado, en castellano y sin eufemismos.
 *
 * NEGATIVO merece la explicacion larga porque es el unico que parece un error de
 * sistema y no lo es: se vendio algo antes de cargar el ingreso, que en un local
 * pasa todos los dias. No hay que "arreglarlo" borrando nada; hay que cargar la
 * compra que faltaba.
 */
export const AYUDA_ESTADO: Record<EstadoStock, string> = {
  OK: "Hay stock suficiente.",
  BAJO: "Hay menos de lo ideal. Conviene reponer sin apuro.",
  CRITICO: "Llegó al mínimo. La próxima venta lo deja sin stock.",
  SIN_STOCK: "No queda ninguna unidad.",
  NEGATIVO:
    "Se vendieron más unidades de las que figuraban cargadas. No es un error del sistema: es una compra que todavía no se ingresó. Cargá el ingreso que falta.",
};

/** La variante de `<Badge />` de cada estado. Sigue MASTER.md sección 12. */
export const VARIANTE_ESTADO: Record<EstadoStock, "success" | "warning" | "destructive"> = {
  OK: "success",
  BAJO: "warning",
  CRITICO: "warning",
  SIN_STOCK: "destructive",
  NEGATIVO: "destructive",
};

/** El mismo orden de gravedad que usa `v_alertas_stock`. */
export const GRAVEDAD: Record<EstadoStock, number> = {
  NEGATIVO: 0,
  SIN_STOCK: 1,
  CRITICO: 2,
  BAJO: 3,
  OK: 4,
};

/** Los estados que muestran las alertas, del peor al menos grave. */
export const ESTADOS_ALERTA: ReadonlyArray<EstadoStock> = [
  "NEGATIVO",
  "SIN_STOCK",
  "CRITICO",
  "BAJO",
];

export function ordenarPorGravedad<T extends { estado: EstadoStock; nombre: string }>(
  filas: ReadonlyArray<T>,
): T[] {
  return [...filas].sort(
    (a, b) => GRAVEDAD[a.estado] - GRAVEDAD[b.estado] || a.nombre.localeCompare(b.nombre, "es"),
  );
}

/**
 * Los negativos arriba, el resto como venia.
 *
 * La tabla del inventario se ordena por valor, que es la pregunta que
 * contesta ("cuanta plata hay"). Pero un negativo vale menos que cero y
 * quedaria al final, que es donde nadie mira, y es justamente lo que hay que
 * atender primero. Se lo sube sin tocar el orden de los demas.
 */
export function negativosPrimero<T extends { estado: EstadoStock }>(filas: ReadonlyArray<T>): T[] {
  return [
    ...filas.filter((f) => f.estado === "NEGATIVO"),
    ...filas.filter((f) => f.estado !== "NEGATIVO"),
  ];
}

export function contarPorEstado(
  filas: ReadonlyArray<{ estado: EstadoStock }>,
): Record<EstadoStock, number> {
  const cuenta: Record<EstadoStock, number> = {
    OK: 0,
    BAJO: 0,
    CRITICO: 0,
    SIN_STOCK: 0,
    NEGATIVO: 0,
  };
  for (const f of filas) cuenta[f.estado] += 1;
  return cuenta;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stock valorizado
// ─────────────────────────────────────────────────────────────────────────────

export interface TotalesStock {
  /** Cuantos productos TIENEN unidades: los que lista la tabla, no el catalogo. */
  productos: number;
  unidades: number;
  /**
   * El MISMO inventario a costo de reposicion, leido en pesos y leido en
   * dolares. No son dos plata distintas y no se suman: son dos formas de mirar
   * la misma mercaderia. La vista ya trae las dos columnas convertidas con la
   * cotizacion vigente; aca solo se acumulan por separado.
   */
  costoArs: number;
  costoUsd: number;
  /** Idem, pero a precio de venta: cuanto se facturaria si se vendiera todo. */
  ventaArs: number;
  ventaUsd: number;
  /**
   * LO VENDIDO SIN STOCK, APARTE (0121_5). Un producto en -1 restaba una
   * unidad y su costo del valor, y sumaba uno a «productos con unidades»: la
   * cabecera decia «699 u. en 149 productos» con 700 u. en 148. No es
   * mercaderia en el local sino una compra que falta cargar; se cuenta en
   * positivo y se muestra en su renglon, que lleva al filtro Negativo.
   */
  sinStockProductos: number;
  sinStockUnidades: number;
  sinStockCostoArs: number;
  sinStockCostoUsd: number;
  /**
   * Lo vendido sin stock que es A PEDIDO: el total del local deja afuera esa
   * seccion, pero el filtro Negativo —adonde lleva el renglon— la muestra. La
   * cinta decia «1 u.» y la tabla listaba 2; se cuenta aparte para nombrarla.
   */
  sinStockAPedidoUnidades: number;
}

const EN_CERO: TotalesStock = {
  productos: 0,
  unidades: 0,
  costoArs: 0,
  costoUsd: 0,
  ventaArs: 0,
  ventaUsd: 0,
  sinStockProductos: 0,
  sinStockUnidades: 0,
  sinStockCostoArs: 0,
  sinStockCostoUsd: 0,
  sinStockAPedidoUnidades: 0,
};

function redondearTotales(t: TotalesStock): TotalesStock {
  return {
    productos: t.productos,
    unidades: redondear(t.unidades),
    costoArs: redondear(t.costoArs),
    costoUsd: redondear(t.costoUsd),
    ventaArs: redondear(t.ventaArs),
    ventaUsd: redondear(t.ventaUsd),
    sinStockProductos: t.sinStockProductos,
    sinStockUnidades: redondear(t.sinStockUnidades),
    sinStockCostoArs: redondear(t.sinStockCostoArs),
    sinStockCostoUsd: redondear(t.sinStockCostoUsd),
    sinStockAPedidoUnidades: redondear(t.sinStockAPedidoUnidades),
  };
}

/**
 * Cuanta plata hay en cada seccion del mostrador.
 *
 * SE SUMA `v_stock_por_familia` Y NO LAS FILAS QUE ESTAN A LA VISTA. La tabla
 * de Inventario esta paginada: sumar sus filas daba el total de lo que se trajo, no
 * el del inventario. Con 380 productos y una ventana de 300, la tarjeta de
 * arriba decia "300 productos" y el desglose de abajo —que siempre sumo sobre
 * el inventario entero— decia 380. Dos numeros de la misma cosa en la misma
 * pantalla, y el que estaba mal era el mas grande. Ahora los dos salen de la
 * misma vista.
 *
 * El precio: el texto del buscador y el chip de estado ya no mueven estos
 * numeros. No es una perdida — "cuanta plata tengo en mercaderia" no cambia
 * porque alguien escriba "cable" en el buscador. La seccion SI los mueve,
 * porque la seccion es una parte real del local.
 */
export function plataPorSeccion(filas: ReadonlyArray<StockPorFamilia>): Map<Seccion, TotalesStock> {
  const mapa = new Map<Seccion, TotalesStock>();
  for (const f of filas) {
    const clave = seccionDeFamilia(f);
    const acc = mapa.get(clave) ?? { ...EN_CERO };
    acc.productos += aNumero(f.productos);
    acc.unidades += aNumero(f.unidades);
    acc.costoArs += aNumero(f.valor_costo_ars);
    acc.costoUsd += aNumero(f.valor_costo_usd);
    acc.ventaArs += aNumero(f.valor_venta_ars);
    acc.ventaUsd += aNumero(f.valor_venta_usd);
    acc.sinStockProductos += aNumero(f.sin_stock_productos);
    acc.sinStockUnidades += aNumero(f.sin_stock_unidades);
    acc.sinStockCostoArs += aNumero(f.sin_stock_costo_ars);
    acc.sinStockCostoUsd += aNumero(f.sin_stock_costo_usd);
    mapa.set(clave, acc);
  }
  for (const [clave, t] of mapa) mapa.set(clave, redondearTotales(t));
  return mapa;
}

/** Los de una seccion, o los del local entero cuando no hay ninguna elegida. */
export function totalesDeSeccion(
  porSeccion: ReadonlyMap<Seccion, TotalesStock>,
  seccion: "" | Seccion,
): TotalesStock {
  // Con una seccion elegida, el filtro Negativo tambien queda en ella: lo que
  // dice la cinta y lo que lista la tabla ya coinciden, sin aparte.
  if (seccion !== "") return porSeccion.get(seccion) ?? { ...EN_CERO };

  // «A pedido» no entra, igual que en el desglose: no esta en el local. Lo
  // contaba solo la cinta, y las secciones no sumaban el total: una funda a
  // pedido en -1 restaba $ 990 arriba y en ninguna caja de abajo.
  const total = { ...EN_CERO };
  for (const [clave, t] of porSeccion) {
    if (clave === "apedido") {
      total.sinStockAPedidoUnidades += t.sinStockUnidades;
      continue;
    }
    total.productos += t.productos;
    total.unidades += t.unidades;
    total.costoArs += t.costoArs;
    total.costoUsd += t.costoUsd;
    total.ventaArs += t.ventaArs;
    total.ventaUsd += t.ventaUsd;
    total.sinStockProductos += t.sinStockProductos;
    total.sinStockUnidades += t.sinStockUnidades;
    total.sinStockCostoArs += t.sinStockCostoArs;
    total.sinStockCostoUsd += t.sinStockCostoUsd;
  }
  return redondearTotales(total);
}

/** Lo que dejaria vender todo el stock, por moneda. Nunca la resta de una mezcla. */
export function gananciaPotencial(t: TotalesStock): { ars: number; usd: number } {
  return { ars: redondear(t.ventaArs - t.costoArs), usd: redondear(t.ventaUsd - t.costoUsd) };
}

// ─────────────────────────────────────────────────────────────────────────────
// Reposicion
// ─────────────────────────────────────────────────────────────────────────────

export interface GrupoReposicion {
  proveedorId: number | null;
  proveedor: string;
  /** La moneda en la que ese proveedor pasa su lista. `null` = sin proveedor. */
  monedaLista: Moneda | null;
  /** El telefono del proveedor, tal como esta cargado. Sin el no hay WhatsApp. */
  telefono: string | null;
  filas: FilaReposicion[];
  /** Cuantas unidades hay que pedirle en total, con las cantidades ya corregidas. */
  unidades: number;
  /** Cuantos productos estan urgentes: se agotan en menos de 5 dias habiles. */
  urgentes: number;
  /** El costo estimado de esa compra, en las dos monedas. No se suman entre si. */
  costoArs: number;
  costoUsd: number;
}

/**
 * Lo que se cambio a mano en la columna «Pedir», por producto. Lo que no esta
 * aca va con lo sugerido; un 0 saca el renglon del pedido sin borrarlo.
 */
export type CantidadesPedido = Readonly<Record<number, number>>;

export function cantidadAPedir(f: FilaReposicion, cantidades: CantidadesPedido = {}): number {
  return cantidades[f.producto_id] ?? aNumero(f.cantidad_sugerida);
}

/**
 * El costo de UN renglon con la cantidad que se va a pedir. Sale del costo
 * unitario y no de `costo_compra_*` porque ese viene calculado sobre lo
 * sugerido, y la cantidad se puede cambiar.
 */
export function costoRenglon(
  f: FilaReposicion,
  cantidades: CantidadesPedido = {},
): { ars: number; usd: number } {
  const n = cantidadAPedir(f, cantidades);
  return { ars: redondear(aNumero(f.costo_ars) * n), usd: redondear(aNumero(f.costo_usd) * n) };
}

/**
 * El total del pedido en la moneda en que el proveedor pasa su lista, que es en
 * la que se le va a pagar. Sin proveedor (o sin moneda de lista) va en pesos.
 */
export function totalEnLista(g: GrupoReposicion): { monto: number; moneda: Moneda } {
  return g.monedaLista === "USD"
    ? { monto: g.costoUsd, moneda: "USD" }
    : { monto: g.costoArs, moneda: "ARS" };
}

/** El subtotal de un renglon en la moneda de su costo. */
export function subtotalRenglon(
  f: FilaReposicion,
  cantidades: CantidadesPedido = {},
): { monto: number; moneda: Moneda } {
  const c = costoRenglon(f, cantidades);
  return f.costo_moneda === "USD"
    ? { monto: c.usd, moneda: "USD" }
    : { monto: c.ars, moneda: "ARS" };
}

/**
 * Lo pedido por WhatsApp que espera la compra y no tiene nada nuevo que pedir.
 * Si despues del pedido se vendio mas, el renglon vuelve a sugerir: no espera.
 */
export function esperaPedido(f: FilaReposicion): boolean {
  return f.pedido_at !== null && aNumero(f.cantidad_sugerida) === 0;
}

/** Dias que pasaron desde que se pidio. */
export function diasDelPedido(f: FilaReposicion, hoy: Date = new Date()): number {
  if (f.pedido_at === null) return 0;
  return Math.max(0, Math.floor((hoy.getTime() - new Date(f.pedido_at).getTime()) / 86_400_000));
}

/**
 * A que ritmo se vende, en palabras (0140): por semana si sale al menos una
 * por semana, si no por mes. Sin dos semanas de historia el ritmo no dice nada
 * y se muestra lo vendido desde la ultima compra, como antes.
 */
export function ritmoLegible(f: FilaReposicion): { valor: string; detalle: string } {
  if (!f.con_historia) {
    return { valor: formatearNumero(aNumero(f.sin_reponer), 0), detalle: "vendidas" };
  }
  const porDia = aNumero(f.ritmo_diario);
  const rotacion = ROTACION_LEGIBLE[f.rotacion];
  if (porDia * 7 >= 1) {
    return {
      valor: `${formatearNumero(porDia * 7, porDia * 7 < 10 ? 1 : 0)}/sem`,
      detalle: rotacion,
    };
  }
  return { valor: `${formatearNumero(porDia * 30, 1)}/mes`, detalle: rotacion };
}

const ROTACION_LEGIBLE: Record<FilaReposicion["rotacion"], string> = {
  ALTA: "rota mucho",
  MEDIA: "rota normal",
  BAJA: "rota poco",
  SIN_VENTAS: "sin ventas",
};

/**
 * Para cuanto alcanza lo que queda, en palabras. `null` = nada que avisar.
 * Sin historia se sigue diciendo «queda poco» cuando es urgente (0102).
 */
export function alcanceLegible(f: FilaReposicion): string | null {
  if (aNumero(f.stock_actual) <= 0) return "sin stock";
  if (!f.con_historia) return f.urgente ? "queda poco" : null;
  if (f.dias_stock === null) return null;
  const dias = aNumero(f.dias_stock);
  if (dias < 1) return "alcanza menos de 1 día";
  const n = Math.floor(dias);
  return `alcanza ${n} ${n === 1 ? "día" : "días"}`;
}

/** A partir de cuantos dias un pedido que no llego se resalta. */
export const DIAS_PEDIDO_DEMORADO = 7;

/** 0 urgente, 1 pedir, 2 agotado de baja rotacion, 3 pedido esperando. */
function tramoReposicion(f: FilaReposicion): number {
  if (esperaPedido(f)) return 3;
  if (f.urgente) return 0;
  return f.nivel === "AGOTADO" ? 2 : 1;
}

/** Para cuantos dias alcanza; lo que no se vende va al fondo. */
function diasParaOrdenar(f: FilaReposicion): number {
  return f.dias_stock === null ? Number.POSITIVE_INFINITY : aNumero(f.dias_stock);
}

/**
 * Que comprarle a cada proveedor.
 *
 * SE AGRUPA POR PROVEEDOR Y NO POR RUBRO porque una orden de compra se le manda
 * a un proveedor, no a una categoria. La lista plana obliga a leerla entera para
 * armar cada pedido.
 *
 * Adentro de cada grupo manda el nivel (0140): primero lo urgente (se agota
 * en menos de 5 dias habiles), despues lo que hay que pedir, despues lo de baja
 * rotacion que se agoto, y al fondo lo ya pedido que espera la compra. Dentro
 * de cada tramo, lo que menos dias aguanta va primero.
 *
 * Los grupos van primero por urgencia (a quien hay que pedirle ya), despues de
 * la compra mas cara a la mas barata, medido en pesos, porque es la que
 * compromete la caja. «Sin proveedor» va al final siempre, porque no se le
 * puede mandar a nadie.
 */
export function agruparReposicion(
  filas: ReadonlyArray<FilaReposicion>,
  cantidades: CantidadesPedido = {},
): GrupoReposicion[] {
  const mapa = new Map<string, GrupoReposicion>();

  for (const f of filas) {
    const clave = f.proveedor_id === null ? "sin-proveedor" : String(f.proveedor_id);
    let grupo = mapa.get(clave);
    if (!grupo) {
      grupo = {
        proveedorId: f.proveedor_id,
        proveedor: f.proveedor,
        monedaLista: f.moneda_lista,
        telefono: f.proveedor_telefono,
        filas: [],
        unidades: 0,
        urgentes: 0,
        costoArs: 0,
        costoUsd: 0,
      };
      mapa.set(clave, grupo);
    }
    const costo = costoRenglon(f, cantidades);
    grupo.filas.push(f);
    grupo.unidades += cantidadAPedir(f, cantidades);
    if (f.urgente && !esperaPedido(f)) grupo.urgentes += 1;
    grupo.costoArs += costo.ars;
    grupo.costoUsd += costo.usd;
  }

  const grupos = [...mapa.values()];
  for (const g of grupos) {
    g.unidades = redondear(g.unidades);
    g.costoArs = redondear(g.costoArs);
    g.costoUsd = redondear(g.costoUsd);
    g.filas = [...g.filas].sort(
      (a, b) =>
        tramoReposicion(a) - tramoReposicion(b) ||
        diasParaOrdenar(a) - diasParaOrdenar(b) ||
        aNumero(b.sin_reponer) - aNumero(a.sin_reponer) ||
        a.nombre.localeCompare(b.nombre, "es"),
    );
  }

  return grupos.sort(
    (a, b) =>
      Number(a.proveedorId === null) - Number(b.proveedorId === null) ||
      b.urgentes - a.urgentes ||
      b.costoArs - a.costoArs ||
      a.proveedor.localeCompare(b.proveedor, "es"),
  );
}

/**
 * El pedido como se le escribe a un proveedor: cantidad, nombre y, si lo
 * tiene, el codigo con el que el proveedor lo conoce, que es lo que le ahorra
 * preguntar «¿cual?». Sin precios: el precio lo pone su lista.
 */
export function mensajePedido(
  grupo: GrupoReposicion,
  nombre: (f: FilaReposicion) => string,
  cantidades: CantidadesPedido = {},
): string {
  const renglones = grupo.filas
    .filter((f) => cantidadAPedir(f, cantidades) > 0)
    .map((f) => {
      const codigo = f.codigo_externo ? ` (${f.codigo_externo})` : "";
      return `• ${formatearNumero(cantidadAPedir(f, cantidades), 0)} × ${nombre(f)}${codigo}`;
    });
  return ["Hola! Te paso un pedido:", "", ...renglones, "", "Gracias!"].join("\n");
}

/** El total de la reposicion completa, por moneda. */
export function totalReposicion(grupos: ReadonlyArray<GrupoReposicion>): {
  unidades: number;
  ars: number;
  usd: number;
  proveedores: number;
} {
  return {
    proveedores: grupos.length,
    unidades: redondear(grupos.reduce((a, g) => a + g.unidades, 0)),
    ars: redondear(grupos.reduce((a, g) => a + g.costoArs, 0)),
    usd: redondear(grupos.reduce((a, g) => a + g.costoUsd, 0)),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Movimientos de stock
// ─────────────────────────────────────────────────────────────────────────────

export type TipoIngreso = "COMPRA" | "AJUSTE" | "DEVOLUCION" | "SALDO_INICIAL" | "OTRO";

export const TIPOS_INGRESO: ReadonlyArray<{
  tipo: TipoIngreso;
  label: string;
  ayuda: string;
  /** Si por defecto esa clase de ingreso pisa el costo de reposicion. */
  actualizaCosto: boolean;
}> = [
  {
    tipo: "COMPRA",
    label: "Compra",
    ayuda: "Mercadería que se le compró a un proveedor.",
    actualizaCosto: true,
  },
  {
    tipo: "DEVOLUCION",
    label: "Devolución",
    ayuda: "Un cliente devolvió algo que ya se le había vendido.",
    actualizaCosto: false,
  },
  {
    tipo: "AJUSTE",
    label: "Ajuste de inventario",
    ayuda: "Apareció mercadería que el sistema no tenía contada. No cambia el costo.",
    actualizaCosto: false,
  },
  {
    tipo: "SALDO_INICIAL",
    label: "Saldo inicial",
    ayuda: "Carga de arranque: lo que ya había en el local antes de usar el sistema.",
    actualizaCosto: true,
  },
  {
    tipo: "OTRO",
    label: "Otro",
    ayuda: "Cualquier otro ingreso de unidades.",
    actualizaCosto: false,
  },
];

export type TipoSalida = "AJUSTE" | "MERMA" | "GARANTIA" | "REPARACION" | "RETIRO_SOCIO" | "OTRO";

/**
 * Las salidas que se cargan a mano.
 *
 * VENTA NO ESTA, Y NO ES UN OLVIDO. La salida por venta la genera `emitir_venta`
 * junto con el comprobante, en la misma transaccion. Cargarla suelta descontaria
 * el stock DOS VECES: una por el ajuste y otra por la venta. El backend tampoco
 * la acepta.
 */
export const TIPOS_SALIDA: ReadonlyArray<{ tipo: TipoSalida; label: string; ayuda: string }> = [
  { tipo: "MERMA", label: "Merma o rotura", ayuda: "Se rompió, se perdió o quedó inservible." },
  {
    tipo: "GARANTIA",
    label: "Garantía",
    ayuda: "Se entregó una unidad para cubrir la garantía de otra.",
  },
  {
    tipo: "REPARACION",
    label: "Uso en taller",
    ayuda: "Se usó un repuesto propio en una reparación.",
  },
  {
    tipo: "AJUSTE",
    label: "Ajuste de inventario",
    ayuda: "El conteo físico dio menos que el sistema.",
  },
  {
    tipo: "RETIRO_SOCIO",
    label: "Retiro de un socio",
    ayuda: "Un socio se lleva el producto. Va a su cuenta en Socios, al costo de hoy.",
  },
  { tipo: "OTRO", label: "Otro", ayuda: "Cualquier otra salida que no sea una venta." },
];

/**
 * Si una salida se lleva mas de lo que hay, y el motivo no lo permite.
 *
 * Una merma, una garantia, un repuesto usado en el taller o el retiro de un
 * socio son cosas que salen FISICAMENTE del local: no se puede romper ni
 * llevar una unidad que no esta. Antes solo se pedia cantidad mayor a cero, y
 * romper 7 de una billetera con 5 dejaba el stock en -2 con un aviso que
 * hablaba de «falta ingresar una compra», que para una rotura no significa
 * nada. El ajuste de inventario y «Otro» siguen pudiendo pasar de largo: son
 * las correcciones, y ahi el negativo tiene lectura.
 */
export function salidaSuperaElStock(tipo: TipoSalida, stock: number, cantidad: number): boolean {
  if (tipo === "AJUSTE" || tipo === "OTRO") return false;
  return cantidad > Math.max(stock, 0);
}

/**
 * De cuanto a cuanto queda el stock despues del movimiento.
 *
 * Se muestra en el paso de revision porque el numero que importa no es el que se
 * escribe (la cantidad) sino el que queda, y son distintos.
 */
export function stockResultante(
  actual: number,
  cantidad: number,
  signo: 1 | -1,
): { antes: number; despues: number; quedaNegativo: boolean } {
  const despues = redondear(actual + signo * cantidad);
  return { antes: redondear(actual), despues, quedaNegativo: despues < 0 };
}

/**
 * El conteo fisico traducido a movimiento.
 *
 * QUIEN CUENTA UN ESTANTE NO PIENSA EN DELTAS. Piensa "hay siete". El sistema
 * necesita "entran dos" o "salen tres": la misma informacion escrita al reves y
 * con una resta en el medio. Esa resta es justo la que sale mal cuando el
 * estante dice siete, la pantalla dice cinco y hay un cliente esperando — y sale
 * mal en silencio, porque un ingreso de 7 sobre un stock de 5 deja 12 y nadie se
 * entera hasta el proximo conteo.
 *
 * Aca se hace la resta una sola vez y se elige el movimiento. No es una forma
 * nueva de tocar el stock: termina en el mismo ingreso o en la misma salida de
 * siempre, con su rastro. Lo unico que cambia es cual de los dos numeros escribe
 * la persona.
 *
 * EL AJUSTE POR CONTEO NUNCA LLEVA COSTO. Contar unidades no es comprarlas: un
 * costo en este movimiento pisaria el costo de reposicion y repriciaria el
 * producto sin que nadie lo haya pedido.
 */
export interface AjustePorConteo {
  antes: number;
  contado: number;
  /** `contado - antes`. Positivo: habia de mas. Negativo: faltan. */
  diferencia: number;
  sentido: "ingreso" | "salida" | "sin_cambio";
  /** El valor absoluto de la diferencia: la cantidad del movimiento. */
  cantidad: number;
}

export function ajustePorConteo(actual: number, contado: number): AjustePorConteo {
  const antes = redondear(actual);
  const fin = redondear(contado);
  const diferencia = redondear(fin - antes);
  return {
    antes,
    contado: fin,
    diferencia,
    sentido: diferencia > 0 ? "ingreso" : diferencia < 0 ? "salida" : "sin_cambio",
    cantidad: Math.abs(diferencia),
  };
}

export interface ImpactoCosto {
  costoAnterior: number;
  monedaAnterior: Moneda;
  costoNuevo: number;
  monedaNueva: Moneda;
  /** El precio de venta que rige hoy. */
  precioAntes: number;
  /** El precio que va a regir despues de que el ingreso pise el costo. */
  precioDespues: number;
  diferencia: number;
  diferenciaPct: number;
  /** `true` si el producto tiene precio manual: entonces el precio NO se mueve. */
  precioCongeladoPorManual: boolean;
  monedaPrecio: Moneda;
}

/**
 * Que le pasa al precio de venta si este ingreso actualiza el costo.
 *
 * ESTE ES EL AVISO QUE FALTA EN CASI TODOS LOS SISTEMAS. Cargar una compra
 * parece una operacion de deposito, pero por defecto pisa el costo de reposicion
 * del producto, y el trigger de precios repricia la venta en el mismo commit. Se
 * entra a cargar cuantas cajas llegaron y se sale con el precio de venta
 * cambiado.
 *
 * El unico caso en que no pasa es el del producto con `precio_manual`: ahi el
 * costo cambia (y con el, el margen) pero el precio queda donde estaba. Tambien
 * se avisa, porque es la otra mitad de la misma sorpresa.
 */
export function impactoDelIngreso(params: {
  producto: Producto;
  costoUnitario: number | null;
  costoMoneda: Moneda;
  actualizaCosto: boolean;
  cotizacion: number;
}): ImpactoCosto | null {
  const { producto, costoUnitario, costoMoneda, actualizaCosto, cotizacion } = params;
  if (!actualizaCosto || costoUnitario === null) return null;

  const costoAnterior = aNumero(producto.costo);
  const precioAntes = aNumero(producto.precio_venta);
  const hayManual = producto.precio_manual !== null;

  const nuevo = calcularPrecio({
    costo: costoUnitario,
    costoMoneda,
    markupTipo: producto.markup_tipo,
    markupValor: aNumero(producto.markup_valor),
    markupMoneda: producto.markup_moneda ?? costoMoneda,
    precioMoneda: producto.precio_moneda,
    redondeo: producto.redondeo,
    precioManual: null,
    cotizacion,
  });

  const precioDespues = hayManual ? precioAntes : nuevo.precioVenta;
  const diferencia = redondear(precioDespues - precioAntes);

  // Sin nada que avisar, no hay aviso. Ingresar al mismo costo de hoy mostraba
  // «Esto cambia el precio de venta… baja el precio (+0,00 %)», y con precio a
  // mano «El costo cambia» cuando no cambiaba. Solo cuando no cambia NADA: si
  // cambia el costo y el redondeo deja el precio igual, el costo de reposicion
  // se pisa igual y eso se tiene que ver.
  const mismoCosto =
    costoMoneda === producto.costo_moneda && redondear(costoUnitario) === redondear(costoAnterior);
  if (mismoCosto && (hayManual || diferencia === 0)) return null;

  return {
    costoAnterior,
    monedaAnterior: producto.costo_moneda,
    costoNuevo: costoUnitario,
    monedaNueva: costoMoneda,
    precioAntes,
    precioDespues,
    diferencia,
    diferenciaPct: precioAntes > 0 ? redondear((diferencia / precioAntes) * 100) : 0,
    precioCongeladoPorManual: hayManual,
    monedaPrecio: producto.precio_moneda,
  };
}

/**
 * El costo del ingreso llevado a la moneda del costo actual, para poder
 * compararlos. Sin esto, "antes US$ 150 / ahora $ 240.000" no dice nada.
 */
export function costoComparable(
  costo: number,
  desde: Moneda,
  hacia: Moneda,
  cotizacion: number,
): number {
  if (desde === hacia || cotizacion <= 0) return redondear(costo);
  return convertir(costo, desde, hacia, cotizacion);
}

// ─────────────────────────────────────────────────────────────────────────────
// La linea de tiempo: como se lee cada fila
// ─────────────────────────────────────────────────────────────────────────────

const NOMBRE_DE_TIPO: Readonly<Record<string, string>> = {
  COMPRA: "Compra",
  VENTA: "Venta",
  DEVOLUCION: "Devolución",
  SALDO_INICIAL: "Saldo inicial",
  MERMA: "Merma",
  GARANTIA: "Garantía",
  REPARACION: "Reparación",
  RETIRO_SOCIO: "Retiro de socio",
  AJUSTE: "Ajuste",
  OTRO: "Otro",
};

/**
 * Las referencias con que un conteo firma sus ajustes.
 *
 * RELEVAMIENTO se queda aunque la pantalla ya no exista: son los ajustes que
 * quedaron firmados asi cuando se conto el catalogo entero, y el historial de
 * stock los sigue mostrando. Sacarla no borraria esos movimientos: los dejaria
 * diciendo "Ajuste" en vez de "Conteo".
 */
const REFERENCIAS_DE_CONTEO: ReadonlySet<string> = new Set(["CONTEO", "RELEVAMIENTO"]);

/**
 * El texto de la columna "Que paso". Un AJUSTE que vino de contar se llama
 * Conteo: es lo que la persona hizo, no la categoria interna.
 */
export function describirMovimiento(
  m: Pick<MovimientoStock, "sentido" | "tipo" | "referencia"> & { socio?: string | null },
): string {
  // El retiro de mercaderia dice de que socio es. Antes los de Ale y los de
  // Emi se leian iguales, con quien los cargo al lado.
  if (m.tipo === "RETIRO_SOCIO" && m.socio) {
    return `${NOMBRE_DE_TIPO[m.tipo] ?? m.tipo} · ${m.socio}`;
  }
  if (
    m.tipo === "AJUSTE" &&
    m.referencia &&
    REFERENCIAS_DE_CONTEO.has(m.referencia.toUpperCase())
  ) {
    return "Conteo";
  }
  // La compra que se anota al vender a pedido (0037) no es mercaderia que
  // entro: el equipo lo tiene el proveedor y se esta esperando (0111). Decia
  // «Compra +1» en el mismo instante que la venta y se leia como que habia
  // llegado.
  if (m.tipo === "COMPRA" && m.referencia?.toUpperCase().startsWith("A PEDIDO ")) {
    return "Pedido al proveedor";
  }
  return NOMBRE_DE_TIPO[m.tipo] ?? m.tipo;
}

/**
 * El orden en que se leen los movimientos: el mas nuevo arriba.
 *
 * La base los manda por fecha, y dos del mismo producto en el MISMO instante
 * —la compra provisoria y la venta de un a pedido— salian en cualquier orden.
 * Con el ingreso arriba, el ultimo acumulado a la vista no era el del final y
 * el aviso de «los movimientos no suman» saltaba sin razon. En el mismo
 * instante, primero entra y despues sale (0125): leido de arriba abajo, la
 * salida va arriba. Lo demas queda como vino.
 */
export function ordenarMovimientos(filas: ReadonlyArray<MovimientoStock>): MovimientoStock[] {
  return [...filas].sort((a, b) => {
    const fa = Date.parse(a.fecha);
    const fb = Date.parse(b.fecha);
    if (fa !== fb) return fb - fa;
    if (a.producto_id !== b.producto_id || a.sentido === b.sentido) return 0;
    return a.sentido === "SALIDA" ? -1 : 1;
  });
}

/**
 * Cuanto se aparta `stock_actual` del ultimo acumulado. `null` si coinciden.
 *
 * Las filas vienen del mas nuevo al mas viejo: la primera es el ultimo
 * movimiento. Sin filas, el acumulado es cero y toda existencia es
 * diferencia. Sin stock conocido no hay con que comparar.
 */
export function diferenciaConStock(
  filas: ReadonlyArray<Pick<MovimientoStock, "stock_despues">>,
  stockActual: Numerico | null,
): number | null {
  if (stockActual === null) return null;
  const ultimo = filas[0] ? aNumero(filas[0].stock_despues) : 0;
  const diferencia = redondear(aNumero(stockActual) - ultimo);
  return diferencia === 0 ? null : diferencia;
}
